import { IconeCarteira, IconeConfiguracao, IconeConta, IconeIa, IconeLote, IconeUsuarios } from '../components/Icones'
import { TEXTOS } from './glossario'
import type { GrupoDeNavegacao } from './navegacao'

/**
 * Os grupos de administração, análise por IA e a conta pessoal. Importe sempre por `./navegacao`.
 *
 * Eram um grupo só, "Administração", com seis itens sem parentesco: quem acessa, os parâmetros do
 * contrato, o custeio da IA e a conta da própria pessoa — e o dono chamou aquilo de "meio
 * misturado". Aqui viram três blocos, cada um com uma pergunta: "quem acessa e com que regras?",
 * "quanto a IA custa e o que ela pode gastar?" e "e a minha conta?". As rotas, as permissões e o
 * lado do contrato de cada item NÃO mudaram: só o grupo, a ordem, o rótulo e o ícone.
 */

/** Grupo "Administração": acesso e parâmetros. Só quem administra o sistema vê. */
export const ADMINISTRACAO: GrupoDeNavegacao = {
  id: 'administracao',
  rotulo: 'Administração',
  itens: [
    {
      id: 'admin-usuarios',
      rotulo: TEXTOS.usuarios.titulo,
      para: '/admin/usuarios',
      icone: IconeUsuarios,
      // O servidor declara esta permissão para quem tem ADMIN. Continua sendo conveniência de
      // menu: quem digitar a rota chega na tela e recebe a recusa do servidor.
      permissao: 'USUARIOS_ADMINISTRAR',
      situacao: 'pronto',
      resumo: 'Quem tem acesso ao sistema e o que cada perfil pode fazer.',
      noSistemaAnterior: 'https://sistemagestao.niteroi.rj.gov.br/users',
    },
    {
      id: 'admin-referencias',
      rotulo: TEXTOS.referencias.titulo,
      para: '/admin/referencias',
      icone: IconeConfiguracao,
      organizacao: 'CONTRATANTE',
      permissao: 'USUARIOS_ADMINISTRAR',
      situacao: 'pronto',
      resumo:
        'Os critérios com que o sistema lê o contrato, o calendário de dias não úteis que decide o prazo dos ' +
        'chamados, o prazo dos relatórios da contratada e as listas dos campos de escolha.',
    },
  ],
}

/**
 * Grupo "Análise por IA": o dinheiro e o gasto, longe da fiscalização.
 *
 * Fica fora de "Contrato e fiscalização" de propósito, e o motivo é o mais forte do sistema: é o
 * ÚNICO lugar com valor em reais relativo à IA. Um R$ ao lado da fiscalização é lido como custo do
 * CONTRATO com a contratada — risco de leitura, e não de dado, que nenhum aviso resolve (V31). A
 * ordem é saldo, depois gasto, depois a ação que gasta.
 */
export const ANALISE_POR_IA: GrupoDeNavegacao = {
  id: 'analise-por-ia',
  rotulo: 'Análise por IA',
  itens: [
    {
      id: 'admin-credito-da-ia',
      rotulo: TEXTOS.creditoDaIa.titulo,
      para: '/admin/credito-da-ia',
      icone: IconeCarteira,
      permissao: 'USUARIOS_ADMINISTRAR',
      situacao: 'pronto',
      resumo: TEXTOS.creditoDaIa.ajuda,
    },
    {
      id: 'admin-uso-da-ia',
      rotulo: TEXTOS.usoDaIa.titulo,
      para: '/admin/uso-da-ia',
      icone: IconeIa,
      permissao: 'USUARIOS_ADMINISTRAR',
      situacao: 'pronto',
      resumo: 'Quantas leituras por IA o sistema fez em cada mês, e quanto elas consumiram.',
    },
    {
      // A tela de confirmação mostra a estimativa em reais: ou o pedido é do administrador, ou o
      // fiscal confirmaria um gasto sem ver quanto custa. O botão de um chamado por vez continua
      // sendo dele.
      id: 'admin-lotes-de-analise',
      rotulo: TEXTOS.loteDeAnalise.titulo,
      para: '/admin/lotes-de-analise',
      icone: IconeLote,
      permissao: 'USUARIOS_ADMINISTRAR',
      situacao: 'pronto',
      resumo: TEXTOS.loteDeAnalise.ajuda,
    },
  ],
}

/**
 * A conta da própria pessoa, no rodapé do menu — sem cabeçalho de grupo.
 *
 * Estava em "Administração", com ícone de cadeado: quem não administra nada via um grupo chamado
 * Administração com um item só dentro, e o cadeado no resto do sistema quer dizer "bloqueada para
 * produção". Continua no registro (e não só no menu do nome) para a aba do navegador e a faixa de
 * cima seguirem dizendo "Minha conta" quando a tela está aberta.
 */
export const CONTA: GrupoDeNavegacao = {
  id: 'conta',
  rotulo: TEXTOS.minhaConta,
  itens: [
    {
      // Sem link para o sistema anterior de propósito: a conta de lá é outra conta. Mandar alguém
      // trocar a senha no sistema antigo achando que troca a daqui é um jeito de perder o acesso.
      id: 'perfil',
      rotulo: TEXTOS.minhaConta,
      para: '/perfil',
      icone: IconeConta,
      situacao: 'pronto',
      resumo: 'Seus dados, o seu lado no contrato e a troca da sua senha.',
    },
  ],
}
